import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

const SwatchHeading = styled.div`
  font-size: 1rem;
  font-weight: normal;
  margin: 0 0 0.25rem 0;
`;

const SwatchColorsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SwatchColor = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  background: ${props => props.backgroundColor};
  opacity: ${props => (props.disabled ? '.25' : '1')};
  border: ${props =>
    props.active
      ? `solid 3px ${props.borderColor}`
      : `solid 1px ${props.borderColor}`};
  border-radius: 50%;
  padding: 0.25rem;
  margin-bottom: 1rem;
  margin-right: 0.5rem;
  cursor: ${props => (props.disabled ? 'auto' : 'pointer')};

  &:last-child {
    margin-right: 0;
  }

  box-sizing: border-box;
  position: relative;
  border: none;
  box-shadow: ${props =>
    props.active
      ? `0 0 0 3px ${props.borderColor}`
      : `0 0 0 1px ${props.borderColor}`};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.5);
    background-image: linear-gradient(
      to top left,
      transparent 45%,
      #000 50%,
      #000 50%,
      transparent 55%
    );
    border-radius: inherit;
    opacity: 0.7;
    display: ${props => (props.strikeThrough ? 'inherit' : 'none')};
  }
`;

const parseSwatchColor = optionValue => {
  const [value, hexCode] = optionValue.split(':');
  return hexCode || value;
};

const getSwatchName = optionValue => {
  const [value] = optionValue.split(':');
  return value;
};

function Swatch({
  name,
  values,
  overrideAvailability,
  activeValue,
  variants,
  findVariantByOptions,
  activeSelectedOptions,
  optionChangeHandler,
  borderColor,
}) {
  const { tt } = useUiComponentsInternationalContext();

  const handleChange = variantSku => {
    optionChangeHandler(variantSku);
  };

  // as we loop modify activeSelectedOptions single value to match what we are looping over
  // so we can find matched variant
  function updateValueOnOptions(v) {
    // Updated: activeSelectedOptions should be using options of current variant, not the first variant
    // as it might be missing options
    const findVariantWithOption = variants.find(variant =>
      variant.selectedOptions.find(opt => opt.name === name && opt.value === v)
    );

    const findActiveSelectedOption = activeSelectedOptions.find(
      so => so.name === name
    );

    if (findActiveSelectedOption) {
      return activeSelectedOptions?.map(so => {
        const copy = { ...so };
        if (copy.name === name) {
          copy.value = v;
        }
        return copy;
      });
    }

    if (findVariantWithOption) {
      return findVariantWithOption.selectedOptions?.map(so => {
        const copy = { ...so };
        if (copy.name === name) {
          copy.value = v;
        }
        return copy;
      });
    }
    return undefined;
  }

  const FormatFocusAriaLabel = color =>
    `${tt(s => s.productCommon.colorAriaLabel)} ${color}`;
  return (
    <div>
      <SwatchHeading>
        {name}:{' '}
        {activeValue &&
          getSwatchName(
            activeSelectedOptions?.find(so => so.name === name)?.value
          )}
      </SwatchHeading>
      <SwatchColorsWrapper
        role="radiogroup"
        aria-label={tt(s => s.productCommon.colorsAriaLabel)}
      >
        {values.map(optionValue => {
          const matchedVariant = findVariantByOptions(
            variants,
            updateValueOnOptions(optionValue)
          );

          return (
            <SwatchColor
              key={optionValue}
              role="radio"
              name="radioswatch"
              backgroundColor={parseSwatchColor(optionValue)}
              borderColor={borderColor}
              active={matchedVariant?.sku === activeValue}
              aria-checked={matchedVariant?.sku === activeValue}
              strikeThrough={
                overrideAvailability ? false : !matchedVariant?.availableForSale
              }
              aria-label={FormatFocusAriaLabel(getSwatchName(optionValue))}
              onClick={() => handleChange(matchedVariant?.sku)}
            />
          );
        })}
      </SwatchColorsWrapper>
    </div>
  );
}
Swatch.propTypes = {
  name: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  variants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  findVariantByOptions: PropTypes.func.isRequired,
  optionChangeHandler: PropTypes.func.isRequired,
  overrideAvailability: PropTypes.bool.isRequired,
  activeSelectedOptions: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, value: PropTypes.string })
  ),
  activeValue: PropTypes.string,
  borderColor: PropTypes.string,
};

Swatch.defaultProps = {
  borderColor: '#aaa',
  activeValue: '',
  activeSelectedOptions: null,
};
export default Swatch;
